import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Alert,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { doc, updateDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { canAccessFeature, normalizePermissionLevel } from '../utils/permissions';

// Permission levels with their capabilities
const PERMISSION_LEVELS = {
  owner: {
    label: 'Owner',
    description: '100% access to all features and settings',
    icon: <AdminPanelSettingsIcon />,
    color: 'error',
    capabilities: [
      'Full access to all features',
      'Manage all settings and configurations',
      'View and edit all employee data',
      'Manage schedules and assignments',
      'Access financial data and wages',
      'Delete employees and data',
      'Manage other users\' permissions',
    ]
  },
  manager: {
    label: 'Manager',
    description: 'All access except Settings Central',
    icon: <ManageAccountsIcon />,
    color: 'warning',
    capabilities: [
      'View and manage employee data',
      'Create and edit schedules',
      'View wages and financial data',
      'Manage assignments and roles',
      'Access analytics and reports',
      'Cannot access Settings Central',
      'Cannot manage permissions',
    ]
  },
  schedule_manager: {
    label: 'Schedule Manager',
    description: 'View schedule, make schedule adjustments',
    icon: <ScheduleIcon />,
    color: 'info',
    capabilities: [
      'View and edit schedules',
      'Manage shift assignments',
      'View employee availability',
      'Cannot see wages or financial data',
      'Cannot access Settings Central',
      'Cannot manage employee personal data',
      'Limited access to Employee Central',
    ]
  },
  employee: {
    label: 'Employee',
    description: 'Access to own dashboard only',
    icon: <PersonIcon />,
    color: 'success',
    capabilities: [
      'View own schedule and shifts',
      'Update own availability',
      'View own profile information',
      'Request time off',
      'Cannot access other employees\' data',
      'Cannot access admin features',
      'Cannot edit schedules',
    ]
  }
};

export default function EmployeePermissionsSection({ employeeId }) {
  const [employee, setEmployee] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState('employee');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [userPermission, setUserPermission] = useState('employee');

  useEffect(() => {
    try {
      const adminUser = localStorage.getItem('adminUser');
      if (!adminUser) return;
      const parsed = JSON.parse(adminUser);
      setUserPermission(normalizePermissionLevel(parsed?.permissionLevel));
    } catch (e) {
      setUserPermission('employee');
    }
  }, []);

  // Load employee data
  useEffect(() => {
    if (!employeeId) return;

    let unsub = null;
    let mounted = true;

    (async () => {
      // Resolve tenantId
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for permissions listener', e);
        }
      }

      if (!tenantId) {
        setLoading(false);
        return;
      }

      try {
        const ref = doc(db, `tenants/${tenantId}/employees`, employeeId);
        unsub = onSnapshot(ref, (snap) => {
          if (!mounted) return;
          if (snap.exists()) {
            const data = snap.data();
            setEmployee(data);
            setSelectedPermission((data.permissionLevel || 'employee').toString().toLowerCase());
          }
          setLoading(false);
        }, (err) => {
          console.warn('permissions listener error', err);
          setLoading(false);
        });
      } catch (e) {
        console.warn('Failed to attach permissions listener', e);
        setLoading(false);
      }
    })();

    return () => { mounted = false; if (typeof unsub === 'function') unsub(); };
  }, [employeeId]);

  const handleSavePermissions = async () => {
    if (!employeeId) return;
    if (!canAccessFeature(userPermission, 'user_permissions')) {
      setSaveMessage({ type: 'error', text: 'Only Owners can manage user permissions.' });
      return;
    }

    setSaving(true);
    try {
      // Resolve tenant and write to tenant-scoped employee doc
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        try {
          const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
          const utSnap = await getDoc(utRef);
          if (utSnap.exists()) {
            const ut = utSnap.data();
            tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
          }
        } catch (e) {
          console.warn('Could not resolve tenant for permissions update', e);
        }
      }
      if (!tenantId) {
        throw new Error('Tenant not found');
      }
      await updateDoc(doc(db, `tenants/${tenantId}/employees`, employeeId), {
        permissionLevel: selectedPermission,
        updatedAt: new Date()
      });
      // Show success message or handle success
      setEmployee((prev) => ({ ...prev, permissionLevel: selectedPermission }));
      // Optionally show success alert
      setSaveMessage({ type: 'success', text: 'Permissions updated successfully.' });
    } catch (error) {
      console.error('Error updating permissions:', error);
      // Handle error
      setSaveMessage({ type: 'error', text: error.message || 'Failed to update permissions.' });
    } finally {
      setSaving(false);
    }
  };

  // Defensive: ensure we always have a permissions mapping even if the stored key is capitalized
  const keyForSelected = (selectedPermission || 'employee').toString().toLowerCase();
  const currentPermissions = PERMISSION_LEVELS[keyForSelected] || PERMISSION_LEVELS['employee'];
  const hasChanges = employee && selectedPermission !== ((employee?.permissionLevel || 'employee').toString().toLowerCase());
  const currentPermissionKey = ((employee?.permissionLevel || 'employee').toString().toLowerCase());
  const activeStoredPermissions = PERMISSION_LEVELS[currentPermissionKey] || PERMISSION_LEVELS.employee;
  const canManagePermissions = canAccessFeature(userPermission, 'user_permissions');
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 4,
          border: '1px solid rgba(59,130,246,0.14)',
          background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,233,0.08) 52%, rgba(16,185,129,0.06) 100%)',
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.06)',
          mb: 2.5,
        }}
      >
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: 1.4, color: '#2563eb' }}>
          ACCESS CONTROL
        </Typography>
        <Typography sx={{ mt: 0.45, fontSize: { xs: '1.9rem', md: '2.4rem' }, lineHeight: 1.05, fontWeight: 900 }}>
          Employee Permissions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 760, lineHeight: 1.75 }}>
          Choose the employee’s permission level, review exactly what they can access, and confirm the change before saving.
        </Typography>
      </Box>

      <Alert
        severity="warning"
        sx={{
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid rgba(245, 158, 11, 0.22)',
          bgcolor: 'rgba(255,247,237,0.92)',
        }}
      >
        <Typography variant="body2">
          <strong>Important:</strong> Changing permissions will affect what this employee can access in the system.
          Make sure you understand the implications before saving changes.
        </Typography>
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(360px, 0.95fr)' }, gap: 2.5 }}>
        {/* Permission Selection */}
        <Card
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.06)',
            bgcolor: 'rgba(255,255,255,0.9)',
          }}
        >
          <CardContent sx={{ p: { xs: 1.6, md: 2.1 } }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1.38rem', mb: 0.45 }}>
              Select Permission Level
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.7 }}>
              Select the access level that best matches this employee’s responsibilities.
            </Typography>
            
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={selectedPermission}
                onChange={(e) => setSelectedPermission(e.target.value)}
              >
                {Object.entries(PERMISSION_LEVELS).map(([key, level]) => (
                  <FormControlLabel
                    key={key}
                    value={key}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        {level.icon}
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 800 }}>
                              {level.label}
                            </Typography>
                            {currentPermissionKey === key && (
                              <Chip
                                label="Current"
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  bgcolor: alpha('#0f172a', 0.08),
                                  color: '#0f172a',
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {level.description}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ 
                      alignItems: 'stretch',
                      mb: 1.1,
                      mx: 0,
                      px: 1.1,
                      py: 1,
                      borderRadius: 3,
                      border: selectedPermission === key ? 2 : 1,
                      borderColor: selectedPermission === key ? `${level.color}.main` : 'rgba(148,163,184,0.22)',
                      backgroundColor: selectedPermission === key ? alpha(
                        level.color === 'error' ? '#ef4444'
                          : level.color === 'warning' ? '#f59e0b'
                          : level.color === 'info' ? '#0ea5e9'
                          : '#22c55e',
                        0.07
                      ) : 'rgba(255,255,255,0.7)',
                      boxShadow: selectedPermission === key ? `0 14px 28px ${alpha(
                        level.color === 'error' ? '#ef4444'
                          : level.color === 'warning' ? '#f59e0b'
                          : level.color === 'info' ? '#0ea5e9'
                          : '#22c55e',
                        0.08
                      )}` : 'none',
                      '&:hover': {
                        backgroundColor: selectedPermission === key ? undefined : 'rgba(248,250,252,0.92)'
                      },
                      '& .MuiFormControlLabel-label': {
                        width: '100%',
                      },
                    }}
                    disabled={!canManagePermissions}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        {/* Permission Details */}
        <Card
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.06)',
            bgcolor: 'rgba(255,255,255,0.9)',
          }}
        >
          <CardContent sx={{ p: { xs: 1.6, md: 2.1 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.8 }}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.6 }}>
                  {currentPermissions.icon || <PersonIcon />}
                  <Typography sx={{ fontWeight: 900, fontSize: '1.38rem' }}>
                    {currentPermissions.label}
                  </Typography>
                  <Chip 
                    label={selectedPermission === currentPermissionKey ? 'Current' : 'Pending change'} 
                    color={selectedPermission === currentPermissionKey ? 'default' : 'primary'}
                    size="small"
                    sx={{ fontWeight: 800 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55, lineHeight: 1.6 }}>
                  {currentPermissions.description}
                </Typography>
              </Box>
              {hasChanges && (
                <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: '#2563eb', flexShrink: 0 }}>
                  {(PERMISSION_LEVELS[currentPermissionKey]?.icon || <PersonIcon fontSize="small" />)}
                  <ArrowForwardIcon fontSize="small" />
                  {currentPermissions.icon || <PersonIcon fontSize="small" />}
                </Stack>
              )}
            </Stack>

            <Divider sx={{ mb: 1.6 }} />

            <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: 1, color: 'text.secondary', mb: 1 }}>
              CAPABILITIES
            </Typography>
            
            <List dense sx={{ py: 0 }}>
              {currentPermissions.capabilities.map((capability, index) => {
                const isRestricted = capability.includes('Cannot') || capability.includes('Limited');
                return (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 0.65,
                      alignItems: 'flex-start',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.15 }}>
                      {isRestricted ? (
                        <CancelIcon color="error" fontSize="small" />
                      ) : (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={capability}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: {
                          lineHeight: 1.55,
                          color: isRestricted ? 'text.secondary' : 'text.primary',
                          fontWeight: isRestricted ? 500 : 600,
                        }
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Save Button */}
      {hasChanges && (
        <Box
          sx={{
            mt: 2.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2,
            flexWrap: 'wrap',
            p: 1.6,
            borderRadius: 3,
            border: '1px solid rgba(37,99,235,0.14)',
            bgcolor: 'rgba(37,99,235,0.04)',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setSelectedPermission(employee?.permissionLevel || 'employee')}
            disabled={saving}
            sx={{ borderRadius: 999, fontWeight: 800 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePermissions}
            disabled={saving || !canManagePermissions}
            sx={{ borderRadius: 999, px: 2.1, fontWeight: 800 }}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </Box>
      )}

      {/* Save result message */}
      {saveMessage && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={saveMessage.type === 'success' ? 'success' : 'error'} sx={{ borderRadius: 3 }}>
            {saveMessage.text}
          </Alert>
        </Box>
      )}

      {/* Current Status */}
      <Card
        sx={{
          mt: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.05)',
          bgcolor: 'rgba(248,250,252,0.88)',
        }}
      >
        <CardContent sx={{ p: { xs: 1.6, md: 2.1 } }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: 1, color: 'text.secondary', mb: 1.05 }}>
            CURRENT STATUS
          </Typography>
          <Stack direction="row" spacing={1.2} alignItems="center">
            {activeStoredPermissions.icon || <PersonIcon />}
            <Box>
              <Typography sx={{ fontWeight: 800 }}>
                This employee currently has {activeStoredPermissions.label} permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {activeStoredPermissions.description}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
